import { useMemo, useRef } from 'react';
import type Highcharts from 'highcharts';
import { setLineChartSelectionSyncSuppressed } from './lineChartExport';
import { isLegendAxisDivider } from './doubleYAxisLegend';

export type LineChartSelectedSeriesNames = string[] | null;

type LineChartSelectionBridgeContext = {
  selectedSeriesNames: LineChartSelectedSeriesNames;
  baseSeriesNames: string[];
  setSelectedSeriesNames: (next: LineChartSelectedSeriesNames) => void;
  resolveVisibleNamesWhenNull?: () => string[];
  collapseFullSelectionToNull?: boolean;
};

function isExportableChartSeries(series: Highcharts.Series): boolean {
  const name = series.name;
  if (!name || /^Navigator \d+$/.test(name) || isLegendAxisDivider(name)) {
    return false;
  }
  if ((series.options as { custom?: { isMeanAggregate?: boolean } })?.custom?.isMeanAggregate) {
    return false;
  }
  return true;
}

export function getVisibleLineChartSelection(
  selectedSeriesNames: LineChartSelectedSeriesNames,
  baseSeriesNames: string[],
): string[] {
  if (selectedSeriesNames === null) {
    return [...baseSeriesNames];
  }
  if (!Array.isArray(selectedSeriesNames)) {
    return [...baseSeriesNames];
  }
  return baseSeriesNames.filter(name => selectedSeriesNames.includes(name));
}

export function isLineChartSeriesVisible(
  seriesName: string,
  selectedSeriesNames: LineChartSelectedSeriesNames,
): boolean {
  if (selectedSeriesNames === null) {
    return true;
  }
  if (!Array.isArray(selectedSeriesNames) || selectedSeriesNames.length === 0) {
    return false;
  }
  return selectedSeriesNames.includes(seriesName);
}

function resolveVisibleNamesForLegendToggle(
  selectedSeriesNames: LineChartSelectedSeriesNames,
  baseSeriesNames: string[],
  currentVisibleNames: string[] | undefined,
  resolveVisibleNamesWhenNull?: () => string[],
): string[] {
  if (Array.isArray(selectedSeriesNames)) {
    return getVisibleLineChartSelection(selectedSeriesNames, baseSeriesNames);
  }

  if (selectedSeriesNames === null && resolveVisibleNamesWhenNull) {
    const defaultPartial = resolveVisibleNamesWhenNull().filter(name => baseSeriesNames.includes(name));
    const fromChart = currentVisibleNames?.filter(name => baseSeriesNames.includes(name)) ?? [];

    if (fromChart.length === 0 || fromChart.length === baseSeriesNames.length) {
      return defaultPartial;
    }

    return fromChart;
  }

  if (currentVisibleNames !== undefined) {
    return currentVisibleNames.filter(name => baseSeriesNames.includes(name));
  }

  return getVisibleLineChartSelection(selectedSeriesNames, baseSeriesNames);
}

export function resolveNextLineChartSelection(
  selectedSeriesNames: LineChartSelectedSeriesNames,
  baseSeriesNames: string[],
  clickedSeriesName: string,
  options?: {
    resolveVisibleNamesWhenNull?: () => string[];
    currentVisibleNames?: string[];
    clickedSeriesVisible?: boolean;
    collapseFullSelectionToNull?: boolean;
  },
): LineChartSelectedSeriesNames {
  if (!baseSeriesNames.includes(clickedSeriesName)) {
    return selectedSeriesNames;
  }

  const {
    resolveVisibleNamesWhenNull,
    currentVisibleNames,
    clickedSeriesVisible,
    collapseFullSelectionToNull = true,
  } = options ?? {};

  const visibleNames = resolveVisibleNamesForLegendToggle(
    selectedSeriesNames,
    baseSeriesNames,
    currentVisibleNames,
    resolveVisibleNamesWhenNull,
  );
  const nextVisibleSet = new Set(visibleNames);

  if (clickedSeriesVisible === true) {
    nextVisibleSet.delete(clickedSeriesName);
  } else if (clickedSeriesVisible === false) {
    nextVisibleSet.add(clickedSeriesName);
  } else if (nextVisibleSet.has(clickedSeriesName)) {
    nextVisibleSet.delete(clickedSeriesName);
  } else {
    nextVisibleSet.add(clickedSeriesName);
  }

  const nextVisible = baseSeriesNames.filter(name => nextVisibleSet.has(name));

  if (nextVisible.length === 0) {
    return [];
  }
  if (nextVisible.length === baseSeriesNames.length) {
    return collapseFullSelectionToNull ? null : nextVisible;
  }
  return nextVisible;
}

export function applyLineChartSelectionToChart(
  chart: Highcharts.Chart,
  baseSeriesNames: string[],
  selectedSeriesNames: LineChartSelectedSeriesNames,
  options?: { redraw?: boolean; explicitVisibleNames?: string[] },
): boolean {
  void baseSeriesNames;
  let changed = false;

  setLineChartSelectionSyncSuppressed(chart, true);

  try {
    chart.series.forEach(series => {
      if (!isExportableChartSeries(series)) {
        return;
      }

      const shouldShow =
        options?.explicitVisibleNames !== undefined
          ? options.explicitVisibleNames.includes(series.name)
          : isLineChartSeriesVisible(series.name, selectedSeriesNames);
      if (series.visible !== shouldShow) {
        series.setVisible(shouldShow, false);
        changed = true;
      }
    });

    if (changed && options?.redraw !== false) {
      chart.redraw(false);
    }
  } finally {
    setLineChartSelectionSyncSuppressed(chart, false);
  }

  return changed;
}

export function getVisibleLineChartSeriesNames(chart: Highcharts.Chart): string[] {
  return (
    chart.series
      ?.filter(isExportableChartSeries)
      ?.filter(series => series.visible !== false)
      ?.map(series => series.name)
      ?.filter((name): name is string => Boolean(name)) ?? []
  );
}

export function adaptLineChartLegendItemClickForLegendEvents(
  legendItemClick: (event: Highcharts.SeriesLegendItemClickEventObject) => false,
) {
  return function legendEventsItemClick(event: {
    legendItem?: Highcharts.Series;
    preventDefault?: () => void;
  }): boolean | void {
    const series = event.legendItem;
    if (!series?.name || isLegendAxisDivider(series.name)) {
      return false;
    }

    return legendItemClick({
      target: series,
    } as Highcharts.SeriesLegendItemClickEventObject);
  };
}

export function handleLineChartLegendItemClick(
  event: Highcharts.SeriesLegendItemClickEventObject,
  selectedSeriesNames: LineChartSelectedSeriesNames,
  baseSeriesNames: string[],
  setSelectedSeriesNames: (next: LineChartSelectedSeriesNames) => void,
  options?: {
    resolveVisibleNamesWhenNull?: () => string[];
    collapseFullSelectionToNull?: boolean;
  },
): false {
  const clickedSeriesName = event.target.name;
  if (!clickedSeriesName || isLegendAxisDivider(clickedSeriesName)) {
    return false;
  }

  const chart = event.target.chart;
  const currentVisibleNames = chart ? getVisibleLineChartSeriesNames(chart) : undefined;
  const nextSelection = resolveNextLineChartSelection(selectedSeriesNames, baseSeriesNames, clickedSeriesName, {
    resolveVisibleNamesWhenNull: options?.resolveVisibleNamesWhenNull,
    currentVisibleNames,
    clickedSeriesVisible: event.target.visible === true,
    collapseFullSelectionToNull: options?.collapseFullSelectionToNull,
  });
  setSelectedSeriesNames(nextSelection);

  if (chart) {
    const explicitVisibleNames = Array.isArray(nextSelection)
      ? nextSelection
      : resolveVisibleNamesForLegendToggle(
          nextSelection,
          baseSeriesNames,
          currentVisibleNames,
          options?.resolveVisibleNamesWhenNull,
        );
    applyLineChartSelectionToChart(chart, baseSeriesNames, nextSelection, {
      explicitVisibleNames,
    });
  }

  return false;
}

export function createLineChartSelectionBridge() {
  const context: LineChartSelectionBridgeContext = {
    selectedSeriesNames: null,
    baseSeriesNames: [],
    setSelectedSeriesNames: () => undefined,
    resolveVisibleNamesWhenNull: undefined,
    collapseFullSelectionToNull: true,
  };

  const legendItemClick = function legendItemClick(event: Highcharts.SeriesLegendItemClickEventObject) {
    return handleLineChartLegendItemClick(
      event,
      context.selectedSeriesNames,
      context.baseSeriesNames,
      context.setSelectedSeriesNames,
      {
        resolveVisibleNamesWhenNull: context.resolveVisibleNamesWhenNull,
        collapseFullSelectionToNull: context.collapseFullSelectionToNull,
      },
    );
  };

  return {
    legendItemClick,
    update(next: LineChartSelectionBridgeContext) {
      context.selectedSeriesNames = next.selectedSeriesNames;
      context.baseSeriesNames = next.baseSeriesNames;
      context.setSelectedSeriesNames = next.setSelectedSeriesNames;
      context.resolveVisibleNamesWhenNull = next.resolveVisibleNamesWhenNull;
      context.collapseFullSelectionToNull = next.collapseFullSelectionToNull;
    },
  };
}

type UseLineChartSelectionSyncParams = {
  baseSeriesNames: string[];
  selectedSeriesNames: LineChartSelectedSeriesNames;
  setSelectedSeriesNames: (next: LineChartSelectedSeriesNames) => void;
  resolveVisibleNamesWhenNull?: () => string[];
  collapseFullSelectionToNull?: boolean;
};

export function useLineChartSelectionSync({
  baseSeriesNames,
  selectedSeriesNames,
  setSelectedSeriesNames,
  resolveVisibleNamesWhenNull,
  collapseFullSelectionToNull,
}: UseLineChartSelectionSyncParams): (event: Highcharts.SeriesLegendItemClickEventObject) => false {
  const bridgeRef = useRef(createLineChartSelectionBridge());

  bridgeRef.current.update({
    selectedSeriesNames,
    baseSeriesNames,
    setSelectedSeriesNames,
    resolveVisibleNamesWhenNull,
    collapseFullSelectionToNull,
  });

  return bridgeRef.current.legendItemClick;
}

export function useLineChartLegendClickHandlers({
  baseSeriesNames,
  selectedSeriesNames,
  setSelectedSeriesNames,
  resolveVisibleNamesWhenNull,
  collapseFullSelectionToNull,
}: UseLineChartSelectionSyncParams) {
  const seriesLegendItemClick = useLineChartSelectionSync({
    baseSeriesNames,
    selectedSeriesNames,
    setSelectedSeriesNames,
    resolveVisibleNamesWhenNull,
    collapseFullSelectionToNull,
  });

  const legendEventsItemClick = useMemo(
    () => adaptLineChartLegendItemClickForLegendEvents(seriesLegendItemClick),
    [seriesLegendItemClick],
  );

  return { legendEventsItemClick };
}

export type SyncLineChartSelectedSeriesParams = {
  chart: Highcharts.Chart;
  baseSeriesNames: string[];
  selectedSeriesNames: LineChartSelectedSeriesNames;
  areSelectionsEqual: (a: LineChartSelectedSeriesNames, b: LineChartSelectedSeriesNames) => boolean;
  setSelectedSeriesNames: (next: LineChartSelectedSeriesNames) => void;
  revealedSeriesName?: string;
  allowCollapseToEmpty?: boolean;
  collapseFullSelectionToNull?: boolean;
};

export function syncLineChartSelectedSeriesFromChart({
  chart,
  baseSeriesNames,
  selectedSeriesNames,
  areSelectionsEqual,
  setSelectedSeriesNames,
  revealedSeriesName,
  allowCollapseToEmpty = false,
  collapseFullSelectionToNull = true,
}: SyncLineChartSelectedSeriesParams): void {
  const nextVisibleSeries = getVisibleLineChartSeriesNames(chart);

  if (!nextVisibleSeries.length) {
    if (revealedSeriesName && baseSeriesNames.includes(revealedSeriesName)) {
      setSelectedSeriesNames([revealedSeriesName]);
      return;
    }

    if (selectedSeriesNames === null) {
      return;
    }
    if (Array.isArray(selectedSeriesNames) && selectedSeriesNames.length === 0) {
      return;
    }

    if (allowCollapseToEmpty) {
      setSelectedSeriesNames([]);
    }
    return;
  }

  const baseNameSet = new Set(baseSeriesNames);
  const filteredNextVisible = nextVisibleSeries.filter(name => baseNameSet.has(name));

  if (!filteredNextVisible.length) {
    return;
  }

  if (filteredNextVisible.length === baseSeriesNames.length) {
    if (selectedSeriesNames === null) {
      return;
    }
    setSelectedSeriesNames(collapseFullSelectionToNull ? null : filteredNextVisible);
    return;
  }

  if (areSelectionsEqual(filteredNextVisible, selectedSeriesNames)) {
    return;
  }

  setSelectedSeriesNames(filteredNextVisible);
}

export function scheduleLineChartSelectedSeriesSync(
  chart: Highcharts.Chart,
  sync: (chart: Highcharts.Chart, revealedSeriesName?: string) => void,
  revealedSeriesName?: string,
): void {
  requestAnimationFrame(() => {
    sync(chart, revealedSeriesName);
  });
}
